import { useQueryClient } from '@tanstack/react-query';

import type { GetPDDMetaobjectDiscountResult } from '@getjust/gateway';

import { useFormMessageAtom } from '$src/hooks/state';
import { useCheckoutFeaturesAtom } from '$src/hooks/state/useCheckoutFeaturesAtom';
import { gateway } from '$src/http';

import { useCart } from '../useCart';
import { useShop } from '../useShop';

const PDD_METAOBJECT_DISCOUNT_QUERY_KEY = 'QUERY/PDD_METAOBJECT_DISCOUNT';

export const useGetPDDMetaobjectDiscount = () => {
  const queryClient = useQueryClient();
  const { formMessage } = useFormMessageAtom();
  const { data: shop } = useShop();
  const { data: cart } = useCart();
  const { canUseCheckoutFeature } = useCheckoutFeaturesAtom();

  const isFeatureEnabled = Boolean(
    formMessage?.shopId &&
      shop?.id &&
      cart?.id &&
      canUseCheckoutFeature('zax_checkout_discount_code_add_products'),
  );

  const getDiscountMetaobject = async (discountCode: string) => {
    if (!isFeatureEnabled || !discountCode) return null;

    return queryClient.fetchQuery({
      queryKey: [PDD_METAOBJECT_DISCOUNT_QUERY_KEY, shop!.id, discountCode],
      queryFn: async (): Promise<GetPDDMetaobjectDiscountResult | null> => {
        const response = await gateway.post<GetPDDMetaobjectDiscountResult>(
          `/api/${formMessage?.shopId}/get-pdd-discount-metaobject`,
          { discountCode },
        );
        return response.data?.outcome === 'notFound' ? null : response.data;
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  };

  return { getDiscountMetaobject };
};
